import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, RefreshCw, BookMarked, Search, X, ClipboardCheck, Loader2, GraduationCap, Flame, BookOpen, Type, EyeOff, Laugh, Swords, GitCompare, Clapperboard, Award } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: API_URL, withCredentials: true });

// Per-category icon for the visual template cards
const CAT_ICON = {
  education: GraduationCap,
  mythbusting: Flame,
  storytelling: BookOpen,
  text_on_screen: Type,
  faceless: EyeOff,
  skits_comedy: Laugh,
  showdown: Swords,
  comparison: GitCompare,
  day_in_the_life: Clapperboard,
  authority: Award,
};

// ===== Reusable Proven Viral Templates picker (10 categories + subformats + AI auto-pick) =====
export const ViralFormatPicker = ({
  formats = [],
  value,
  onChange,
  onSuggest,
  nicheAvailable = true,
  prefix = 'format',
  containerTestId = 'viral-format-picker',
  hintText = 'Tip: enter a niche first to let AI choose a format for you.',
}) => {
  const { category = '', subtype = '', faceless = false, textOnScreen = false } = value || {};
  const [expanded, setExpanded] = useState(category || '');
  const [suggesting, setSuggesting] = useState(false);
  const [reason, setReason] = useState('');

  const handleSuggest = async () => {
    if (!onSuggest) return;
    setSuggesting(true);
    setReason('');
    try {
      const s = await onSuggest();
      if (s && s.category) {
        setExpanded(s.category);
        onChange({ category: s.category, subtype: s.subtype || '', faceless, textOnScreen });
        setReason(s.reason || '');
        toast.success('AI picked a viral format for you');
      }
    } catch (e) {
      toast.error('Could not suggest a format');
    } finally {
      setSuggesting(false);
    }
  };

  const cat = formats.find((c) => c.key === expanded);

  return (
    <div className="border border-[#E2E8F0] rounded-xl p-3" data-testid={containerTestId}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div>
          <span className="text-xs font-bold uppercase tracking-wider text-[#0A0F1C]">Proven Viral Templates</span>
          <p className="text-[10px] text-[#64748B]">Pick a proven format — or let AI choose the best one for your niche. Optional.</p>
        </div>
        <button
          type="button"
          onClick={handleSuggest}
          disabled={suggesting || !nicheAvailable}
          data-testid={`${prefix}-autopick-button`}
          title={!nicheAvailable ? 'Enter a niche first' : 'Let AI pick the best format'}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#7C3AED] text-white text-[11px] font-bold disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
        >
          {suggesting ? <RefreshCw size={12} className="animate-spin" aria-hidden="true" /> : <Sparkles size={12} aria-hidden="true" />}
          Let us choose for you
        </button>
      </div>
      {!nicheAvailable && (
        <p className="text-[10px] text-[#94A3B8] mb-2" data-testid={`${prefix}-autopick-hint`}>{hintText}</p>
      )}
      {reason && (
        <p className="text-[10px] italic text-[#7C3AED] mb-2" data-testid={`${prefix}-suggest-reason`}>AI pick: {reason}</p>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid={`${prefix}-category-chips`}>
        {formats.map((c) => {
          const active = category === c.key;
          const Icon = CAT_ICON[c.key] || Sparkles;
          return (
            <button
              key={c.key}
              type="button"
              data-testid={`${prefix}-cat-${c.key}`}
              onClick={() => { setExpanded(expanded === c.key ? '' : c.key); onChange({ category: c.key, subtype: '', faceless, textOnScreen }); }}
              className="group text-left rounded-xl border p-2.5 transition duration-200 hover:-translate-y-0.5 hover:shadow-md focus:outline-none"
              style={{ borderColor: active ? c.accent : '#E2E8F0', backgroundColor: active ? `${c.accent}0F` : '#FFFFFF', boxShadow: active ? `0 0 0 1px ${c.accent}` : undefined }}
            >
              {c.image ? (
                <img src={c.image} alt={`${c.label} illustration`} loading="lazy" width="320" height="112" className="w-full h-14 object-cover rounded-lg mb-1.5" style={{ backgroundColor: `${c.accent}0F` }} />
              ) : (
                <span className="flex items-center justify-center w-8 h-8 rounded-lg mb-1.5" style={{ backgroundColor: `${c.accent}1A`, color: c.accent }}>
                  <Icon size={16} aria-hidden="true" />
                </span>
              )}
              <span className="block text-[11px] font-bold leading-tight break-words" style={{ color: active ? c.accent : '#0A0F1C' }}>{c.label}</span>
              <span className="block text-[10px] text-[#94A3B8] leading-tight mt-0.5">{c.description}</span>
            </button>
          );
        })}
      </div>
      {category && (
        <button
          type="button"
          data-testid={`${prefix}-clear`}
          onClick={() => { onChange({ category: '', subtype: '', faceless, textOnScreen }); setExpanded(''); setReason(''); }}
          className="mt-2 text-[10px] font-semibold text-[#94A3B8] hover:text-[#475569] underline"
        >
          Clear selection
        </button>
      )}
      {cat && (
        <div className="mt-2 grid sm:grid-cols-2 gap-1.5" data-testid={`${prefix}-subformats`}>
          <button
            type="button"
            data-testid={`${prefix}-sub-auto-${cat.key}`}
            onClick={() => onChange({ category: cat.key, subtype: '', faceless, textOnScreen })}
            className="text-left px-2.5 py-1.5 rounded-lg border text-[11px] transition"
            style={{ borderColor: category === cat.key && !subtype ? cat.accent : '#E2E8F0', backgroundColor: category === cat.key && !subtype ? `${cat.accent}0F` : '#FFFFFF' }}
          >
            <span className="font-bold" style={{ color: cat.accent }}>Let us choose within {cat.label}</span>
            <span className="block text-[#64748B]">Best {cat.label} template for your niche</span>
          </button>
          {cat.subformats.map((s) => {
            const on = category === cat.key && subtype === s.key;
            return (
              <button
                key={s.key}
                type="button"
                data-testid={`${prefix}-sub-${s.key}`}
                onClick={() => onChange({ category: cat.key, subtype: s.key, faceless, textOnScreen })}
                className="text-left px-2.5 py-1.5 rounded-lg border text-[11px] transition"
                style={{ borderColor: on ? cat.accent : '#E2E8F0', backgroundColor: on ? `${cat.accent}0F` : '#FFFFFF' }}
              >
                <span className="font-bold text-[#0A0F1C]">{s.label}</span>
                <span className="block text-[#64748B]">{s.description}</span>
              </button>
            );
          })}
        </div>
      )}
      <div className="flex items-center gap-4 mt-3">
        <label className="flex items-center gap-1.5 text-[11px] font-semibold text-[#475569] cursor-pointer" data-testid={`${prefix}-faceless-toggle`}>
          <input type="checkbox" checked={faceless} onChange={(e) => onChange({ category, subtype, faceless: e.target.checked, textOnScreen })} /> Faceless
        </label>
        <label className="flex items-center gap-1.5 text-[11px] font-semibold text-[#475569] cursor-pointer" data-testid={`${prefix}-textonscreen-toggle`}>
          <input type="checkbox" checked={textOnScreen} onChange={(e) => onChange({ category, subtype, faceless, textOnScreen: e.target.checked })} /> Text on screen
        </label>
      </div>
    </div>
  );
};

// ===== Proven Viral Hook Library seed bank (100 fill-in-the-blank hooks) =====
export const HookLibraryPicker = ({ onPick, testidPrefix = 'hook-library', label = 'Hook Library' }) => {
  const [open, setOpen] = useState(false);
  const [library, setLibrary] = useState([]);
  const [q, setQ] = useState('');

  useEffect(() => {
    if (open && library.length === 0) {
      api.get('/api/admin/social-studio/hook-library')
        .then((r) => setLibrary(r.data.library || []))
        .catch(() => toast.error('Could not load hook library'));
    }
  }, [open, library.length]);

  const filtered = library
    .map((g) => ({ ...g, hooks: g.hooks.filter((h) => h.toLowerCase().includes(q.toLowerCase())) }))
    .filter((g) => g.hooks.length);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testidPrefix}-trigger`}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#E2E8F0] text-[#475569] text-[11px] font-bold hover:border-[#1D4ED8] hover:text-[#1D4ED8] transition-colors"
      >
        <BookMarked size={13} aria-hidden="true" /> {label}
      </button>
      {open && (
        <div className="absolute z-30 mt-2 w-[340px] max-h-[380px] overflow-auto bg-white border border-[#E2E8F0] rounded-xl shadow-xl p-3" data-testid={`${testidPrefix}-panel`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold uppercase tracking-wider text-[#0A0F1C]">Proven Hook Library</span>
            <button type="button" onClick={() => setOpen(false)} aria-label="Close" className="text-[#94A3B8] hover:text-[#0A0F1C]"><X size={14} /></button>
          </div>
          <div className="flex items-center gap-1.5 border border-[#E2E8F0] rounded-lg px-2 py-1 mb-2">
            <Search size={12} className="text-[#94A3B8]" aria-hidden="true" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search hooks..."
              data-testid={`${testidPrefix}-search`}
              className="flex-1 text-[11px] outline-none"
            />
          </div>
          {filtered.map((g) => (
            <div key={g.category} className="mb-2">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#7C3AED] mb-1">{g.category}</div>
              <div className="space-y-1">
                {g.hooks.map((h, i) => (
                  <button
                    key={i}
                    type="button"
                    data-testid={`${testidPrefix}-item-${g.category.replace(/\s+/g, '-').toLowerCase()}-${i}`}
                    onClick={() => { onPick(h); setOpen(false); toast.success('Hook added — fill in the blanks'); }}
                    className="w-full text-left text-[11px] text-[#334155] px-2 py-1.5 rounded-md hover:bg-[#EFF6FF] hover:text-[#1D4ED8] transition-colors"
                  >
                    {h}
                  </button>
                ))}
              </div>
            </div>
          ))}
          {filtered.length === 0 && <p className="text-[11px] text-[#94A3B8] py-4 text-center">No hooks match your search.</p>}
        </div>
      )}
    </div>
  );
};

// ===== Viral Reel Checklist (10-point) self-scoring card =====
const REEL_CHECKLIST = [
  { section: 'IDEA', items: [
    { label: 'Topic people actually care about', desc: 'Would a random stranger stop for this?' },
    { label: 'Hook is clear + relatable', desc: 'Understandable in one breath; calls out the ideal viewer / niche pain.' },
    { label: 'Creates curiosity (open loop)', desc: '"Wait… how?" — makes the viewer feel compelled to finish.' },
    { label: 'Promise + one mini problem', desc: 'Outcome promised in the first 3-5s; solves one small, common problem.' },
    { label: 'Proof + credibility shown fast', desc: 'Demo / before-after / results / screen recording within ~2 seconds.' },
  ]},
  { section: 'DELIVERY', items: [
    { label: 'Pattern interrupt in first 0-2s', desc: 'Movement / reveal / bold on-screen text; works with sound OFF.' },
    { label: 'Retention edit — no dead air', desc: 'Something changes every 2-3s; payoff held to the end, no clickbait.' },
    { label: 'Text + captions native and easy', desc: 'Big, high-contrast text; subtitles included; inside safe zones.' },
  ]},
  { section: 'OPTIMIZATION', items: [
    { label: 'Reels settings + sound correct', desc: '9:16 vertical, clean balanced audio, trending/pleasing music.' },
    { label: 'CTA clear + matches content', desc: 'One simple action — Comment / Follow / Save — that fits the content.' },
  ]},
];

export const ReelChecklistCard = () => {
  const [checked, setChecked] = useState({});
  const flat = REEL_CHECKLIST.flatMap((s) => s.items.map((it) => `${s.section}::${it.label}`));
  const score = flat.filter((k) => checked[k]).length;
  const verdict = score >= 8 ? { t: 'Strong post — ship it', c: '#16A34A' } : score >= 6 ? { t: 'Fix the weakest 2', c: '#EA7317' } : { t: 'Rebuild hook + retention first', c: '#E05252' };

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5" data-testid="reel-checklist-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ClipboardCheck size={16} className="text-[#7C3AED]" aria-hidden="true" />
          <h3 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">Viral Reel Checklist</h3>
        </div>
        <div className="text-right">
          <div className="text-lg font-black" style={{ color: verdict.c }} data-testid="reel-checklist-score">{score}/10</div>
          <div className="text-[10px] font-bold" style={{ color: verdict.c }}>{verdict.t}</div>
        </div>
      </div>
      {REEL_CHECKLIST.map((s) => (
        <div key={s.section} className="mb-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">{s.section}</div>
          <div className="space-y-1.5">
            {s.items.map((it) => {
              const key = `${s.section}::${it.label}`;
              return (
                <label key={key} className="flex items-start gap-2 cursor-pointer group" data-testid={`reel-check-${key.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
                  <input type="checkbox" checked={!!checked[key]} onChange={(e) => setChecked({ ...checked, [key]: e.target.checked })} className="mt-0.5" />
                  <span className="text-[12px] leading-tight">
                    <span className="font-semibold text-[#0A0F1C] group-hover:text-[#1D4ED8]">{it.label}</span>
                    <span className="block text-[10px] text-[#64748B]">{it.desc}</span>
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      ))}
      <p className="text-[10px] text-[#94A3B8] border-t border-[#F1F5F9] pt-2 mt-1">Give yourself 1 point per box → 8-10 strong · 6-7 fix weakest 2 · ≤5 rebuild hook + retention first.</p>
    </div>
  );
};

// ===== Generation progress (elapsed timer + animated bar + rotating stages) =====
const GEN_STAGES = [
  'Analyzing your niche & audience...',
  'Shaping the proven viral format...',
  'Writing a scroll-stopping hook...',
  'Structuring problem → solution → CTA...',
  'Adding on-screen text & B-roll ideas...',
  'Scoring for virality...',
  'Polishing the final draft...',
];

export const GenerationProgress = ({ active, label = 'Writing your viral script', expectedSeconds = 55 }) => {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!active) { setElapsed(0); return undefined; }
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [active]);
  if (!active) return null;
  const pct = Math.min(95, Math.round((elapsed / expectedSeconds) * 100));
  const stage = GEN_STAGES[Math.min(GEN_STAGES.length - 1, Math.floor(elapsed / Math.max(1, Math.round(expectedSeconds / GEN_STAGES.length))))];
  return (
    <div className="mt-3 border border-[#DBEAFE] bg-[#F8FBFF] rounded-xl p-4" data-testid="generation-progress">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold text-[#1D4ED8] flex items-center gap-1.5"><Loader2 size={13} className="animate-spin" aria-hidden="true" /> {label}</span>
        <span className="text-[11px] font-mono text-[#64748B]" data-testid="generation-elapsed">{elapsed}s</span>
      </div>
      <div className="h-2 w-full bg-[#E2E8F0] rounded-full overflow-hidden">
        <div className="h-full bg-gradient-to-r from-[#1D4ED8] to-[#7C3AED] rounded-full transition-all duration-1000 ease-linear" style={{ width: `${pct}%` }} />
      </div>
      <p className="text-[11px] text-[#64748B] mt-2" data-testid="generation-stage">
        {stage} <span className="text-[#94A3B8]">Usually ~45-55s — hang tight, this tab isn't frozen.</span>
      </p>
    </div>
  );
};
